<div class="flyout-scrim" data-testid="flyout-scrim" (click)="close()"></div>

<aside
  class="flyout"
  role="dialog"
  aria-modal="true"
  aria-labelledby="hearing-detail-title"
  data-testid="hearing-detail-flyout"
  cdkTrapFocus
  [cdkTrapFocusAutoCapture]="true"
  (click)="$event.stopPropagation()"
>
  <header class="flyout__bar">
    <h2 id="hearing-detail-title" class="flyout__title">Hearing details</h2>
    <button
      type="button"
      class="icon-button flyout__close"
      aria-label="Close hearing details"
      title="Close"
      data-testid="flyout-close"
      (click)="close()"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>
    </button>
  </header>

  <div class="flyout__body">
    <div *ngIf="isLoading()" class="flyout__state" data-testid="flyout-loading">
      <span class="flyout__state-spinner" aria-hidden="true"></span>
      {{ hearingText.loading() }}
    </div>

    <div
      *ngIf="errorState() as error"
      class="flyout__state flyout__state--error"
      role="alert"
      data-testid="flyout-error"
    >
      <p class="flyout__state-title">{{ error.title }}</p>
      <p *ngFor="let message of error.messages">{{ message }}</p>
    </div>

    <ng-container *ngIf="appointment() as appt">
      <section class="identity" aria-labelledby="hearing-person-name" data-testid="flyout-body">
        <div class="identity__heading">
          <div>
            <h3 id="hearing-person-name" class="identity__name" data-testid="flyout-hearing-label">{{ appt.title }}</h3>
            <button
              type="button"
              class="identity__number"
              data-testid="flyout-proceeding-number"
              (click)="openHearingWorkspace()"
            >
              <span>{{ appt.proceedingNumber }}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M14 5h5v5M19 5l-8 8"></path>
                <path d="M19 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"></path>
              </svg>
            </button>
          </div>
          <span class="status-chip" data-testid="flyout-status">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="4" y="5" width="16" height="15" rx="2"></rect><path d="M8 3v4M16 3v4M4 10h16"></path>
            </svg>
            {{ appt.statusLabel }}
          </span>
        </div>
      </section>

      <section class="appointment" aria-label="Appointment">
        <div *ngIf="appointmentDate() as date" class="appointment__date-tile" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <rect x="4" y="5" width="16" height="15" rx="2"></rect><path d="M8 3v4M16 3v4M4 10h16"></path>
          </svg>
          <span>{{ date.weekday }}</span>
          <strong>{{ date.day }}</strong>
          <span>{{ date.monthYear }}</span>
        </div>

        <div class="appointment__details">
          <div>
            <h3 data-testid="flyout-date">{{ appointmentDate()?.compactLabel || appt.scheduledDateLabel }}</h3>
            <p class="appointment__time" data-testid="flyout-time">{{ appt.scheduledTimeLabel }}</p>
          </div>

          <div class="appointment__fact">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0z"></path><circle cx="12" cy="10" r="2.5"></circle>
            </svg>
            <div data-testid="flyout-location">
              <strong>{{ appt.locationNameLabel }}</strong>
              <span *ngIf="appt.roomNameLabel">{{ appt.roomNameLabel }}</span>
            </div>
          </div>

          <div class="appointment__fact">
            <svg *ngIf="appt.formatLabel === 'Video'; else inPersonIcon" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3" y="6" width="13" height="12" rx="2"></rect><path d="M16 10l5-3v10l-5-3z"></path>
            </svg>
            <ng-template #inPersonIcon>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 21V9l8-5 8 5v12M9 21v-7h6v7M2 21h20"></path>
              </svg>
            </ng-template>
            <span data-testid="flyout-format">{{ appt.formatLabel }}</span>
          </div>
        </div>
      </section>

      <section *ngIf="assignedOfficial()" class="hearing-owner" aria-label="Assigned official">
        <div class="hearing-owner__summary">
          <span class="section-icon section-icon--neutral" aria-hidden="true">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"></circle><path d="M4 21a8 8 0 0 1 16 0"></path></svg>
          </span>
          <div>
            <span>Assigned official</span>
            <strong data-testid="flyout-assigned-official">{{ assignedOfficialLabel() }}</strong>
          </div>
        </div>

        <div *ngIf="canAssignOfficial" class="official-controls" data-testid="official-controls">
          <div class="official-controls__actions">
            <button
              type="button"
              class="text-button"
              data-testid="assign-official-toggle"
              [attr.aria-expanded]="showAssignOfficial()"
              (click)="toggleAssignOfficial()"
            >Change official</button>
            <button
              type="button"
              class="text-button text-button--danger"
              data-testid="remove-official"
              [disabled]="isSavingOfficial()"
              (click)="removeOfficial()"
            >Remove</button>
          </div>
          <ng-container *ngTemplateOutlet="officialPicker"></ng-container>
        </div>
      </section>

      <section *ngIf="hasAttentionItems()" class="attention" aria-labelledby="attention-heading">
        <div class="section-heading">
          <svg class="section-heading__warning" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3L2.5 20h19L12 3z"></path><path d="M12 9v5M12 17.5h.01"></path>
          </svg>
          <h3 id="attention-heading">Needs attention</h3>
        </div>

        <div *ngIf="isLoadingOfficial()" class="attention-row">
          <span class="attention-row__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"></circle><path d="M4 21a8 8 0 0 1 16 0"></path></svg>
          </span>
          <span class="attention-row__label">Checking official assignment&hellip;</span>
        </div>

        <div *ngIf="!isLoadingOfficial() && !assignedOfficial()" class="attention-row">
          <span class="attention-row__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"></circle><path d="M4 21a8 8 0 0 1 16 0"></path></svg>
          </span>
          <span class="attention-row__label" data-testid="flyout-assigned-official">Official not assigned</span>
          <div *ngIf="canAssignOfficial" class="official-controls" data-testid="official-controls">
            <button
              type="button"
              class="secondary-button"
              data-testid="assign-official-toggle"
              [attr.aria-expanded]="showAssignOfficial()"
              (click)="toggleAssignOfficial()"
            >Assign official</button>
            <ng-container *ngTemplateOutlet="officialPicker"></ng-container>
          </div>
        </div>

        <div *ngIf="isLoadingEligibility()" class="attention-row" data-testid="flyout-eligibility">
          <span class="attention-row__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><rect x="5" y="3" width="14" height="18" rx="2"></rect><path d="M8 7h8M8 11h2M14 11h2M8 15h2M14 15h2"></path></svg>
          </span>
          <span class="attention-row__label">Checking eligibility&hellip;</span>
        </div>

        <div *ngIf="!schedulingRequestId && !isLoadingEligibility() && !hearingEligibility()" class="attention-row" data-testid="flyout-eligibility">
          <span class="attention-row__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><rect x="5" y="3" width="14" height="18" rx="2"></rect><path d="M8 7h8M8 11h2M14 11h2M8 15h2M14 15h2"></path></svg>
          </span>
          <span class="attention-row__label">Eligibility not calculated</span>
          <button
            type="button"
            class="secondary-button"
            [disabled]="isCalculatingEligibility()"
            data-testid="eligibility-calculate"
            (click)="calculateEligibility()"
          >{{ isCalculatingEligibility() ? "Calculating&hellip;" : "Calculate" }}</button>
        </div>

        <p *ngIf="eligibilityActionMessage() as message" class="inline-message">{{ message }}</p>
        <p *ngIf="officialActionMessage() as message" class="inline-message">{{ message }}</p>
      </section>

      <section class="case-context" aria-labelledby="case-context-heading">
        <div class="section-heading">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 7h7l2 2h9v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z"></path><path d="M3 11h18"></path></svg>
          <h3 id="case-context-heading">Case context</h3>
        </div>

        <div *ngIf="managedSchedulingBasis() as basis" class="context-fact" data-testid="flyout-managed-eligibility">
          <div><span class="context-fact__label">Governed hearing eligibility</span><span class="semantic-chip" [attr.data-tone]="basis.basisIssue ? 'warning' : 'success'">{{basis.basisIssue ? 'Needs review' : 'Eligible'}}</span></div>
          <p *ngIf="basis.dueDate">Due by {{basis.dueDate}}</p><p *ngIf="basis.currentReceiptId">Evidence receipt {{basis.currentReceiptId}}</p><p *ngIf="basis.basisIssue">{{basis.basisIssue}}</p>
        </div>
        <div *ngIf="!schedulingRequestId && hearingEligibility()" class="context-fact" data-testid="flyout-eligibility">
          <div>
            <span class="context-fact__label">Hearing eligibility</span>
            <span class="semantic-chip" [attr.data-tone]="eligibilityTone()">{{ eligibilityStatusLabel() }}</span>
          </div>
          <button
            type="button"
            class="text-button"
            [disabled]="isCalculatingEligibility()"
            data-testid="eligibility-calculate"
            (click)="calculateEligibility()"
          >{{ isCalculatingEligibility() ? "Calculating&hellip;" : "Recalculate" }}</button>
        </div>

        <div class="context-block" data-testid="flyout-charges">
          <h4>Controlling charge</h4>
          <p *ngIf="chargesState() === 'loading'" class="context-empty">Loading charge&hellip;</p>
          <p *ngIf="chargesState() === 'empty'" class="context-empty" data-testid="charges-empty">{{ subjectText.noCharges() }}</p>
          <div *ngIf="controllingCharge() as charge" class="charge" data-testid="charge-row">
            <div>
              <strong>{{ chargeHeadline(charge) }}</strong>
              <span *ngIf="charge.statuteReference">{{ charge.statuteReference }}</span>
            </div>
            <span *ngIf="chargeSeverityLabel(charge) as severity" class="severity-chip">{{ severity }}</span>
          </div>
        </div>

        <div class="context-block documents-summary" data-testid="flyout-documents-summary">
          <div class="documents-summary__heading">
            <h4>{{ documentText.documents() }}</h4>
            <span class="count-badge" [attr.aria-label]="documentCount() + ' documents'">{{ documentCount() }}</span>
          </div>

          <p *ngIf="documentsState() === 'loading'" class="context-empty">Loading documents&hellip;</p>
          <p *ngIf="documentsState() === 'error'" class="context-empty">Documents are temporarily unavailable.</p>
          <p *ngIf="documentsState() === 'empty'" class="context-empty">No documents are associated yet.</p>

          <ul *ngIf="documentsState() === 'ready'" class="document-list" data-testid="documents-body">
            <li *ngFor="let document of visibleDocuments(); trackBy: trackByDocument" data-testid="document-summary-row">
              <span class="document-list__icon" [attr.data-kind]="documentIconKind(document)" aria-hidden="true">
                <svg *ngIf="documentIconKind(document) === 'people'" viewBox="0 0 24 24">
                  <circle cx="9" cy="8" r="3"></circle><circle cx="17" cy="9" r="2.5"></circle><path d="M3 20a6 6 0 0 1 12 0M14 15a5 5 0 0 1 7 4.5"></path>
                </svg>
                <svg *ngIf="documentIconKind(document) === 'legal'" viewBox="0 0 24 24">
                  <path d="M12 3v18M7 6h10M4 18h16"></path><path d="M7 6l-4 7h8L7 6zM17 6l-4 7h8l-4-7z"></path>
                </svg>
                <svg *ngIf="documentIconKind(document) === 'file'" viewBox="0 0 24 24">
                  <path d="M6 3h8l4 4v14H6z"></path><path d="M14 3v5h5M9 12h6M9 16h6"></path>
                </svg>
              </span>
              <span class="document-list__title">{{ document.displayName || "Document" }}</span>
              <span class="document-status" [attr.data-status]="documentStatus(document).key">{{ documentStatus(document).label }}</span>
            </li>
          </ul>

          <button
            *ngIf="incarceratedPersonId() !== null"
            type="button"
            class="documents-summary__link"
            data-testid="view-all-documents"
            (click)="openAllDocuments()"
          >
            View all documents
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7"></path></svg>
          </button>
        </div>
      </section>
    </ng-container>
  </div>

  <footer *ngIf="appointment()" class="flyout__footer" data-testid="flyout-footer">
    <div *ngIf="showMoveDate()" class="flyout__move-form" data-testid="move-date-form">
      <label class="flyout__move-label" for="move-date-input">New date &amp; time</label>
      <input
        id="move-date-input"
        type="datetime-local"
        class="flyout__move-input"
        [value]="moveDateInputValue"
        (change)="onMoveDateInput($any($event.target).value)"
        data-testid="move-date-input"
      />
      <div class="flyout__form-actions">
        <button
          type="button"
          class="primary-button"
          data-testid="action-move-confirm"
          [disabled]="!moveDateFormValues.scheduledStart"
          (click)="confirmMoveDate()"
        >Confirm new date</button>
        <button type="button" class="secondary-button" data-testid="action-move-cancel" (click)="toggleMoveDate()">Cancel</button>
      </div>
    </div>

    <div *ngIf="showCancelConfirm()" class="flyout__confirm" data-testid="cancel-confirm">
      <div>
        <strong>{{ hearingText.unscheduleConfirmTitle() }}</strong>
        <p data-testid="cancel-confirm-message"><ng-container *ngIf="appointment()?.title as title">{{ title }}: </ng-container>{{ hearingText.unscheduleConfirmMessage() }}</p>
      </div>
      <div class="flyout__form-actions">
        <button type="button" class="danger-button" data-testid="cancel-confirm-yes" (click)="confirmUnschedule()">{{ hearingText.confirmUnschedule() }}</button>
        <button type="button" class="secondary-button" data-testid="cancel-confirm-no" (click)="dismissUnschedule()">{{ hearingText.keep() }}</button>
      </div>
    </div>

    <div *ngIf="!showCancelConfirm()" class="flyout__actions" data-testid="flyout-actions">
      <button
        type="button"
        class="primary-button flyout__workspace-action"
        data-testid="action-hearing-input"
        (click)="openHearingWorkspace()"
      >Open hearing workspace</button>

      <button
        *ngIf="canReschedule"
        type="button"
        class="secondary-button flyout__move-action"
        data-testid="action-move-date"
        (click)="toggleMoveDate()"
      >{{ showMoveDate() ? "Cancel move" : "Move date" }}</button>

      <button
        *ngIf="canReschedule"
        type="button"
        class="secondary-button flyout__unschedule-action"
        data-testid="action-unschedule"
        (click)="requestUnschedule()"
      >{{ hearingText.unschedule() }}</button>
    </div>
  </footer>
</aside>

<ng-template #officialPicker>
  <div *ngIf="showAssignOfficial()" class="official-picker" data-testid="assign-official-picker">
    <label for="assign-official-select">{{ hearingText.official() }}</label>
    <select
      id="assign-official-select"
      data-testid="assign-official-select"
      [disabled]="isSavingOfficial()"
      (change)="assignOfficial($any($event.target).value)"
    >
      <option value="">Select an official&hellip;</option>
      <option *ngFor="let official of assignableOfficials(); trackBy: trackByOfficial" [value]="official.userId">{{ officialOptionLabel(official) }}</option>
    </select>
  </div>
</ng-template>
